import { Link, useLocation } from "@tanstack/react-router";
import { cn, useSidebar } from "@/components/kit";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import {
  type BottomNavItem,
  type BottomNavLink,
  bottomNavItems,
} from "./data/bottom-nav-data";

function isLinkItem(item: BottomNavItem): item is BottomNavLink {
  return item.id !== "more";
}

function isBottomNavActive(pathname: string, item: BottomNavLink): boolean {
  if (
    item.excludePaths?.some(
      (path) => pathname === path || pathname.startsWith(`${path}/`),
    )
  ) {
    return false;
  }

  if (pathname === item.url) return true;

  if (item.matchPrefix) {
    return pathname.startsWith(`${item.url}/`);
  }

  return false;
}

export function BottomNav() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const { setOpenMobile } = useSidebar();
  const { hasAll } = usePermissions();

  const visibleItems = bottomNavItems.filter((item) => {
    if (!isLinkItem(item)) return true;
    return hasAll(item.requiredPermissions ?? []);
  });

  if (visibleItems.length === 0) return null;

  return (
    <nav
      aria-label="ເມນູຫຼັກ"
      className="fixed inset-x-0 bottom-0 z-40 border-secondary/20 border-t bg-background/95 pb-[env(safe-area-inset-bottom,0px)] backdrop-blur-md md:hidden"
    >
      <ul className="mx-auto flex h-16 max-w-lg items-stretch justify-around px-1">
        {visibleItems.map((item) => {
          if (!isLinkItem(item)) {
            return (
              <li key={item.id} className="flex min-w-0 flex-1">
                <button
                  type="button"
                  className="flex w-full flex-col items-center justify-center gap-0.5 px-1 text-muted-foreground transition-colors active:bg-muted/60"
                  onClick={() => setOpenMobile(true)}
                >
                  <item.icon className="size-5" />
                  <span className="truncate font-medium text-[10px] leading-tight">
                    {item.title}
                  </span>
                </button>
              </li>
            );
          }

          const active = isBottomNavActive(pathname, item);
          const isCreate = item.id === "new-order";

          return (
            <li key={item.id} className="flex min-w-0 flex-1">
              <Link
                to={item.url}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "flex w-full flex-col items-center justify-center gap-0.5 px-1 transition-colors active:bg-muted/60",
                  active
                    ? "text-secondary"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {isCreate ? (
                  <span
                    className={cn(
                      "flex size-9 items-center justify-center rounded-full shadow-sm",
                      active
                        ? "bg-primary text-primary-foreground"
                        : "bg-primary/90 text-primary-foreground",
                    )}
                  >
                    <item.icon className="size-5" weight="bold" />
                  </span>
                ) : (
                  <item.icon
                    className="size-5"
                    weight={active ? "fill" : "regular"}
                  />
                )}
                <span className="truncate font-medium text-[10px] leading-tight">
                  {item.title}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
